import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Badge,
  Tag,
  Popover,
  ChoiceList,
  Spinner,
  Text,
} from "@shopify/polaris";
import { FilterIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { fetchFavoriteCategories } from "services/files-category.service";

interface FavoriteCategoryFilterProps {
  companyId: string;
  subject?: string;
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  isLoading?: boolean;
}

const FavoriteCategoryFilter: React.FC<FavoriteCategoryFilterProps> = ({
  companyId,
  subject = "customer",
  selectedCategories,
  onCategoriesChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [favoriteCategories, setFavoriteCategories] = useState<any[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [isFilterPopoverActive, setIsFilterPopoverActive] = useState(false);

  // Fetch favorite categories
  useEffect(() => {
    async function loadFavoriteCategories() {
      if (companyId) {
        setIsLoadingFavorites(true);
        try {
          const favorites = await fetchFavoriteCategories(companyId, subject);
          if (favorites && favorites.length > 0) {
            setFavoriteCategories(favorites);
          } else {
            setFavoriteCategories([]);
          }
        } catch (e) {
          setFavoriteCategories([]);
        } finally {
          setIsLoadingFavorites(false);
        }
      }
    }
    loadFavoriteCategories();
  }, [companyId, subject]);

  // Handle category filter change from ChoiceList
  const handleCategoryFilterChange = useCallback(
    (selectedCategoriesList: string[]) => {
      onCategoriesChange(selectedCategoriesList);
    },
    [onCategoriesChange]
  );

  // Handle removing individual filter
  const handleFilterRemove = useCallback(
    (categoryId: string) => {
      onCategoriesChange(selectedCategories.filter((id) => id !== categoryId));
    },
    [selectedCategories, onCategoriesChange]
  );

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    onCategoriesChange([]);
  }, [onCategoriesChange]);

  // Create applied filters for display
  const appliedFilters = useMemo(() => {
    return selectedCategories.map((categoryId) => {
      const category = favoriteCategories.find((cat) => cat.id === categoryId);
      return {
        key: categoryId,
        label: category ? category.title : categoryId,
        onRemove: () => handleFilterRemove(categoryId),
      };
    });
  }, [selectedCategories, favoriteCategories, handleFilterRemove]);

  // Create filter choices from favorite categories
  const categoryFilterChoices = useMemo(() => {
    return favoriteCategories.map((category) => ({
      label: category.title,
      value: category.id,
    }));
  }, [favoriteCategories]);

  if (favoriteCategories.length === 0 && !isLoadingFavorites) {
    return null;
  }

  return (
    <div className="py-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Popover
          active={isFilterPopoverActive}
          activator={
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setIsFilterPopoverActive(!isFilterPopoverActive)}
                icon={<FilterIcon />}
              >
                {t("favorite_categories")}
              </Button>
              {selectedCategories.length > 0 && (
                <Badge tone="info">{String(selectedCategories.length)}</Badge>
              )}
            </div>
          }
          onClose={() => setIsFilterPopoverActive(false)}
          preferredAlignment="left"
        >
          <div className="p-4 min-w-[250px]">
            {isLoadingFavorites ? (
              <div className="p-2.5 text-center">
                <Spinner size="small" />
              </div>
            ) : (
              <ChoiceList
                allowMultiple
                title={t("favorite_categories")}
                choices={categoryFilterChoices}
                selected={selectedCategories}
                onChange={handleCategoryFilterChange}
              />
            )}
          </div>
        </Popover>
      </div>
      {appliedFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Text variant="bodySm" as="span" fontWeight="medium">
            {t("applied_filters") || "Applied filters"}:
          </Text>
          {appliedFilters.map((filter) => (
            <Tag key={filter.key} onRemove={filter.onRemove}>
              {filter.label}
            </Tag>
          ))}
          <Button
            size="slim"
            variant="plain"
            onClick={handleClearAllFilters}
            tone="critical"
          >
            {t("clear_all_filters")}
          </Button>
        </div>
      )}
      {isLoadingFavorites && favoriteCategories.length === 0 && (
        <div className="py-2.5">
          <Spinner size="small" />
        </div>
      )}
    </div>
  );
};

export default FavoriteCategoryFilter;
